<template>
    <div class="page">
        <v-container>
            <v-card :flat="phoneOnly" class="mt-4 px-2" :class="{'soft-shadow': pcOnly, 'transparent': phoneOnly}">
                <v-card-title>Leave Requests</v-card-title>
                <v-card-subtitle>
                    Request a leave from your classes. You can request up to 14 days per year. If you need more days, please contact our support team.
                </v-card-subtitle>
                <v-card-text v-if="summary" class="pt-2 pb-0">
                    <v-alert dense text type="info" color="blue-grey lighten-4" class="mb-0">
                        <div class="d-flex align-center">
                            <v-icon class="me-2" color="blue-grey darken-2">mdi-calendar-clock</v-icon>
                            <span class="medium-text">
                                <strong>Remaining Leave Days:</strong>
                                <span class="font-weight-bold" :class="summary.remaining > 0 ? 'success--text' : 'error--text'">
                                    {{ summary.remaining }} day(s)
                                </span>
                                <span v-if="summary.used > 0" class="muted--text ms-2">
                                    ({{ summary.used }} day(s) used)
                                </span>
                            </span>
                        </div>
                    </v-alert>
                </v-card-text>

                <div v-if="holidays && holidays.length" class="px-4 pb-3">
                    <v-subheader class="px-0">Your Leave Requests</v-subheader>
                    <v-list dense>
                        <v-list-item v-for="holiday in holidays" :key="holiday.id" class="px-0">
                            <v-list-item-content>
                                <v-list-item-title>
                                    {{ holiday.start_date }} to {{ holiday.end_date }}
                                    <v-chip x-small :color="getHolidayStatusColor(holiday.status)" dark class="ml-2">
                                        {{ getHolidayStatusText(holiday.status) }}
                                    </v-chip>
                                </v-list-item-title>
                                <v-list-item-subtitle v-if="holiday.rejection_reason" class="red--text">
                                    Reason: {{ holiday.rejection_reason }}
                                </v-list-item-subtitle>
                                <v-list-item-subtitle v-if="holiday.cancellation_reason" class="orange--text">
                                    Cancellation Reason: {{ holiday.cancellation_reason }}
                                </v-list-item-subtitle>
                            </v-list-item-content>
                            <v-list-item-action v-if="holiday.status === 'pending'">
                                <v-btn icon small @click="cancelHoliday(holiday.id)">
                                    <v-icon small>mdi-close</v-icon>
                                </v-btn>
                            </v-list-item-action>
                        </v-list-item>
                    </v-list>
                </div>

                <v-card-actions class="pb-3 px-4">
                    <v-btn depressed color="primary" :block="phoneOnly" class="white--text px-lg-3" @click="requestLeaveDialog = true">
                        <v-icon left>mdi-calendar-remove</v-icon>
                        Request Leave
                    </v-btn>
                </v-card-actions>
            </v-card>

            <v-dialog v-model="requestLeaveDialog" max-width="500">
                <v-card>
                    <v-card-title>Request Leave</v-card-title>
                    <v-card-text>
                        <v-menu v-model="startMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="startDate"
                                    label="Start Date"
                                    prepend-inner-icon="mdi-calendar"
                                    readonly
                                    outlined
                                    v-bind="attrs"
                                    v-on="on"
                                    hide-details
                                    class="mb-3"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="startDate"
                                :min="minHolidayDate"
                                @input="startMenu = false"
                                color="primary"
                            ></v-date-picker>
                        </v-menu>

                        <v-menu v-model="endMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="endDate"
                                    label="End Date"
                                    prepend-inner-icon="mdi-calendar"
                                    readonly
                                    outlined
                                    v-bind="attrs"
                                    v-on="on"
                                    hide-details
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="endDate"
                                :min="startDate || minHolidayDate"
                                @input="endMenu = false"
                                color="primary"
                            ></v-date-picker>
                        </v-menu>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer />
                        <v-btn text color="muted" @click="requestLeaveDialog = false">Cancel</v-btn>
                        <v-btn text color="primary" :loading="requestingLeave" @click="requestLeave">Submit</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-container>
    </div>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed'
import GlobalMethods from '@/helpers/global-methods'

export default {
    name: 'TeacherLeaveRequests',
    data () {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        return {
            holidays: [],
            summary: null,

            requestLeaveDialog: false,
            startDate: '',
            endDate: '',
            startMenu: false,
            endMenu: false,
            requestingLeave: false,

            minHolidayDate: d.toISOString().substr(0, 10),
        }
    },
    computed: {
        ...GlobalComputed
    },
    methods: {
        ...GlobalMethods,
        loadData(){
            window.axios.get('/teacher/holidays').then(response => {
                this.holidays = response.data.holidays || [];
                this.summary = response.data.summary || null;
            }).catch(() => {
                this.showMsg('Error occurred during fetching leave requests', 'error');
            });
        },
        requestLeave(){
            if (!this.startDate || !this.endDate){
                this.showMsg('Please select both start and end dates', 'error');
                return;
            }

            this.requestingLeave = true;
            window.axios.post('/teacher/holidays', {
                start_date: this.startDate,
                end_date: this.endDate,
            }).then(response => {
                this.requestingLeave = false;
                this.requestLeaveDialog = false;
                this.startDate = '';
                this.endDate = '';
                this.showMsg('Leave request submitted successfully', 'success');
                this.loadData();
            }).catch(error => {
                this.requestingLeave = false;
                if (error.response && error.response.status === 422){
                    const errors = error.response.data.errors || {};
                    const firstKey = Object.keys(errors)[0];
                    const errorMessage = firstKey ? errors[firstKey][0] : (error.response.data.message || 'Error occurred during submitting leave request');
                    this.showMsg(errorMessage, 'error');
                }else{
                    this.showMsg('Error occurred during submitting leave request', 'error');
                }
            });
        },
        getHolidayStatusColor(status){
            const colors = {
                'pending': 'orange',
                'approved': 'green',
                'rejected': 'red',
                'cancelled': 'grey',
            };
            return colors[status] || 'grey';
        },
        getHolidayStatusText(status){
            const texts = {
                'pending': 'Pending',
                'approved': 'Approved',
                'rejected': 'Rejected',
                'cancelled': 'Cancelled',
            };
            return texts[status] || status;
        },
        cancelHoliday(id){
            if (!confirm('Are you sure you want to cancel this leave request?')){
                return;
            }

            window.axios.delete(`/teacher/holidays/${id}`).then(() => {
                this.showMsg('Leave request cancelled successfully', 'success');
                this.loadData();
            }).catch(error => {
                if (error.response && error.response.status === 422){
                    const errors = error.response.data.errors || {};
                    const firstKey = Object.keys(errors)[0];
                    const errorMessage = firstKey ? errors[firstKey][0] : (error.response.data.message || 'Error occurred during cancelling leave request');
                    this.showMsg(errorMessage, 'error');
                }else{
                    this.showMsg('Error occurred during cancelling leave request', 'error');
                }
            });
        },
    },
    mounted(){
        this.loadData();
    }
}
</script>


